import React, { useEffect, useState } from 'react';
import { PromptEditModal } from './PromptEditModal';
import { ResponseViewModal } from './ResponseViewModal';
import { DebugErrorBoundary } from './DebugErrorBoundary';
import { useDebug } from '@/contexts/debug-context';
import { AIRequest } from '@/types/debug';
import { DebugInterceptor } from '@/services/debug-interceptor';

export function DebugModals() {
  const { debugState, handleDebugError, clearCurrentRequest, clearCurrentResponse } = useDebug();
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);

  // Show prompt modal when there's a current request
  useEffect(() => {
    if (debugState.currentRequest) {
      setIsPromptModalOpen(true);
    }
  }, [debugState.currentRequest]);

  // Show response modal when there's a current response
  useEffect(() => {
    if (debugState.currentResponse) {
      setIsResponseModalOpen(true);
    }
  }, [debugState.currentResponse]);

  const handleSendRequest = (modifiedRequest: AIRequest) => {
    try {
      // Clear any timeout
      if ((window as any).__debugRequestTimeout) {
        clearTimeout((window as any).__debugRequestTimeout);
        (window as any).__debugRequestTimeout = null;
      }

      // Use event-based callback if available, otherwise fall back to promise-based
      if ((window as any).__debugRequestResolver) {
        (window as any).__debugRequestResolver(modifiedRequest);
        (window as any).__debugRequestResolver = null;
      }
      
      // Clear the current request from debug state
      clearCurrentRequest();
      setIsPromptModalOpen(false);
    } catch (error) {
      console.error('Error sending debug request:', error);
      handleDebugError(error instanceof Error ? error : new Error('Failed to send debug request'));
    }
  };

  const handleCancelRequest = () => {
    try {
      // Clear any timeout
      if ((window as any).__debugRequestTimeout) {
        clearTimeout((window as any).__debugRequestTimeout);
        (window as any).__debugRequestTimeout = null;
      }

      // Cancel the current request in the interceptor
      DebugInterceptor.cancelCurrentRequest();

      // Use event-based callback if available, otherwise fall back to promise-based
      if ((window as any).__debugCancelResolver) {
        (window as any).__debugCancelResolver();
        (window as any).__debugCancelResolver = null;
      } else if ((window as any).__debugRequestResolver) {
        (window as any).__debugRequestResolver(null);
        (window as any).__debugRequestResolver = null;
      }
      
      // Clear the current request from debug state
      clearCurrentRequest();
      setIsPromptModalOpen(false);
    } catch (error) {
      console.error('Error cancelling debug request:', error);
      handleDebugError(error instanceof Error ? error : new Error('Failed to cancel debug request'));
    }
  };

  const handleClosePromptModal = () => {
    // If modal is closed without action, treat as cancel
    if (isPromptModalOpen) {
      handleCancelRequest();
    }
  };

  const handleCloseResponseModal = () => {
    try {
      // Use event-based callback if available
      if ((window as any).__debugResponseCloseResolver) {
        (window as any).__debugResponseCloseResolver();
        (window as any).__debugResponseCloseResolver = null;
      }
      
      // Clear the current response from debug state
      clearCurrentResponse();
      setIsResponseModalOpen(false);
    } catch (error) {
      console.error('Error closing response modal:', error);
      handleDebugError(error instanceof Error ? error : new Error('Failed to close response modal'));
    }
  };

  // Listen for debug system events
  useEffect(() => {
    const handleDebugSystemError = (event: CustomEvent) => {
      const { error, context } = event.detail;
      console.error(`Debug system error in ${context}:`, error);
      handleDebugError(error);
    };

    const handleDebugRecovery = () => {
      console.log('Debug system recovered, resetting modal state');
      setIsPromptModalOpen(false);
      setIsResponseModalOpen(false);
    };

    const handleShowPromptEditor = (event: CustomEvent) => {
      const { requestId, request, onConfirm, onCancel } = event.detail;
      
      // Store the callbacks for the modal to use
      (window as any).__debugRequestResolver = onConfirm;
      (window as any).__debugCancelResolver = onCancel;
      
      // Set the request in debug state and show modal
      debugState.currentRequest = request;
      setIsPromptModalOpen(true);
    };

    const handleShowResponseViewer = (event: CustomEvent) => {
      const { responseId, response, onClose } = event.detail;
      
      // Store the callback for the modal to use
      (window as any).__debugResponseCloseResolver = onClose;
      
      // Set the response in debug state and show modal
      debugState.currentResponse = response;
      setIsResponseModalOpen(true);
    };

    window.addEventListener('debug:error', handleDebugSystemError as EventListener);
    window.addEventListener('debug:recovered', handleDebugRecovery);
    window.addEventListener('debug:showPromptEditor', handleShowPromptEditor as EventListener);
    window.addEventListener('debug:showResponseViewer', handleShowResponseViewer as EventListener);

    return () => {
      window.removeEventListener('debug:error', handleDebugSystemError as EventListener);
      window.removeEventListener('debug:recovered', handleDebugRecovery);
      window.removeEventListener('debug:showPromptEditor', handleShowPromptEditor as EventListener);
      window.removeEventListener('debug:showResponseViewer', handleShowResponseViewer as EventListener);
    };
  }, [handleDebugError, debugState]);

  const handleDebugComponentError = (error: Error, errorInfo: React.ErrorInfo) => {
    console.error('Debug component error:', error, errorInfo);
    handleDebugError(error);
    
    // Attempt to recover from the error
    DebugInterceptor.recoverFromError();
  };

  return (
    <DebugErrorBoundary onError={handleDebugComponentError}>
      <PromptEditModal
        isOpen={isPromptModalOpen}
        onClose={handleClosePromptModal}
        request={debugState.currentRequest || null}
        onSend={handleSendRequest}
        onCancel={handleCancelRequest}
      />
      
      <ResponseViewModal
        isOpen={isResponseModalOpen}
        onClose={handleCloseResponseModal}
        response={debugState.currentResponse || null}
      />
    </DebugErrorBoundary>
  );
}